/* eslint-disable no-inner-declarations */
import React, { Component } from 'react';
// taken from traveloka www: https://github.com/traveloka/www/issues/4066
let isHoverEnabled = process.env.NODE_ENV !== 'test' ? false : true;
const HOVER_THRESHOLD_MS = 1000;
let lastTouchTimestamp = 0;
function enableHover() {
    if (isHoverEnabled || Date.now() - lastTouchTimestamp < HOVER_THRESHOLD_MS) {
        return;
    }
    isHoverEnabled = true;
}
function disableHover() {
    lastTouchTimestamp = Date.now();
    if (isHoverEnabled) {
        isHoverEnabled = false;
    }
}
export const unregisterHover = () => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        document.removeEventListener('touchstart', disableHover, true);
        document.removeEventListener('touchmove', disableHover, true);
        document.removeEventListener('mousemove', enableHover, true);
    }
};
export const registerHover = () => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        document.addEventListener('touchstart', disableHover, true);
        document.addEventListener('touchmove', disableHover, true);
        document.addEventListener('mousemove', enableHover, true);
    }
};
export function useHoverable(callbacks = {}) {
    const [isHovered, setHovered] = React.useState(false);
    const [showHover, setShowHover] = React.useState(true);
    const { onMouseEnter, onMouseOut } = callbacks;
    const handleMouseEnter = React.useCallback(() => {
        if (isHoverEnabled && !isHovered) {
            setHovered(true);
            onMouseEnter && onMouseEnter();
        }
    }, [isHoverEnabled, isHovered, onMouseEnter]);
    const handleMouseLeave = React.useCallback(() => {
        if (isHovered) {
            setHovered(false);
            onMouseOut && onMouseOut();
        }
    }, [isHovered, onMouseOut]);
    const handleGrant = React.useCallback(() => {
        setShowHover(false);
    }, []);
    const handleRelease = React.useCallback(() => {
        setShowHover(true);
    }, []);
    return [
        showHover && isHovered,
        {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onResponderGrant: handleGrant,
            onResponderRelease: handleRelease,
            onPressIn: handleGrant,
            onPressOut: handleRelease,
        },
    ];
}
export default class Hoverable extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            isHovered: false,
            showHover: true,
        };
        this.handleMouseEnter = (e) => {
            if (isHoverEnabled && !this.state.isHovered) {
                const { onMouseOver } = this.props;
                if (onMouseOver)
                    onMouseOver(e);
                this.setState({ isHovered: true });
            }
        };
        this.handleMouseLeave = (e) => {
            if (this.state.isHovered) {
                const { onMouseOut } = this.props;
                if (onMouseOut)
                    onMouseOut(e);
                this.setState({ isHovered: false });
            }
        };
        this.handleGrant = (child, action, ...args) => {
            this.setState({ showHover: false });
            if (action === 'responder') {
                child.props.onResponderGrant && child.props.onResponderGrant(...args);
            }
            else if (action === 'press') {
                child.props.onPressIn && child.props.onPressIn(...args);
            }
        };
        this.handleRelease = (child, action, ...args) => {
            this.setState({ showHover: true });
            if (action === 'responder') {
                child.props.onResponderRelease && child.props.onResponderRelease(...args);
            }
            else if (action === 'press') {
                child.props.onPressOut && child.props.onPressOut(...args);
            }
        };
    }
    render() {
        const { children } = this.props;
        const child = children(this.state.showHover && this.state.isHovered);
        return React.cloneElement(React.Children.only(child), {
            onMouseEnter: this.handleMouseEnter,
            onMouseLeave: this.handleMouseLeave,
            // prevent hover showing while responder
            onResponderGrant: this.handleGrant.bind(this, child, 'responder'),
            onResponderRelease: this.handleRelease.bind(this, child, 'responder'),
            // if child is Touchable
            onPressIn: this.handleGrant.bind(this, child, 'press'),
            onPressOut: this.handleRelease.bind(this, child, 'press'),
        });
    }
}
